import React, { useState } from 'react';
import { clientApi } from '../services/api';
import { Client } from '../types';
import './ClientForm.css';

interface ClientFormProps {
  onSuccess: () => void;
  client?: Client | null;
  onCancel?: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ onSuccess, client, onCancel }) => {
  const [formData, setFormData] = useState<Omit<Client, 'id' | 'created_at' | 'updated_at'>>({
    name: client?.name || '',
    phone: client?.phone || '',
    email: client?.email || '',
    city: client?.city || '',
    license_plate: client?.license_plate || '',
    vehicle_type: client?.vehicle_type || '',
    motorcycle_brand: client?.motorcycle_brand || '',
    motorcycle_color: client?.motorcycle_color || '',
    motorcycle_year: client?.motorcycle_year || '',
    motorcycle_cc: client?.motorcycle_cc || '',
    rtm_date: client?.rtm_date || '',
    birthday: client?.birthday || '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (client?.id) {
        await clientApi.update(client.id, formData);
      } else {
        await clientApi.create(formData);
      }
      onSuccess();
      if (!client?.id) {
        setFormData({
          name: '',
          phone: '',
          email: '',
          city: '',
          license_plate: '',
          vehicle_type: '',
          motorcycle_brand: '',
          motorcycle_color: '',
          motorcycle_year: '',
          motorcycle_cc: '',
          rtm_date: '',
          birthday: '',
        });
      }
      if (onCancel) {
        onCancel();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="client-form-container">
      <h2>{client?.id ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
      <form onSubmit={handleSubmit} className="client-form">
        <div className="form-group">
          <label htmlFor="name">Nombre *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Teléfono *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder="573001234567"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email || ''}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">Ciudad</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: Bogotá, Medellín, Cali"
          />
        </div>

        <div className="form-group">
          <label htmlFor="license_plate">Placa</label>
          <input
            type="text"
            id="license_plate"
            name="license_plate"
            value={formData.license_plate || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: ABC123, XYZ789"
          />
        </div>

        <div className="form-group">
          <label htmlFor="vehicle_type">Tipo de Vehículo</label>
          <select
            id="vehicle_type"
            name="vehicle_type"
            value={formData.vehicle_type || ''}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Seleccione un tipo</option>
            <option value="teaching">Enseñanza</option>
            <option value="public">Público</option>
            <option value="private">Particular</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="motorcycle_brand">Marca de la Moto</label>
          <input
            type="text"
            id="motorcycle_brand"
            name="motorcycle_brand"
            value={formData.motorcycle_brand || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: Yamaha, Honda, Kawasaki"
          />
        </div>

        <div className="form-group">
          <label htmlFor="motorcycle_color">Color de la Moto</label>
          <input
            type="text"
            id="motorcycle_color"
            name="motorcycle_color"
            value={formData.motorcycle_color || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: Rojo, Negro, Azul"
          />
        </div>

        <div className="form-group">
          <label htmlFor="motorcycle_year">Año de la Moto</label>
          <input
            type="text"
            id="motorcycle_year"
            name="motorcycle_year"
            value={formData.motorcycle_year || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: 2020, 2021, 2022"
          />
        </div>

        <div className="form-group">
          <label htmlFor="motorcycle_cc">Cilindraje (CC)</label>
          <input
            type="text"
            id="motorcycle_cc"
            name="motorcycle_cc"
            value={formData.motorcycle_cc || ''}
            onChange={handleChange}
            disabled={loading}
            placeholder="Ej: 250, 300, 600"
          />
        </div>

        <div className="form-group">
          <label htmlFor="rtm_date">Fecha de RTM</label>
          <input
            type="date"
            id="rtm_date"
            name="rtm_date"
            value={formData.rtm_date || ''}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="birthday">Fecha de Cumpleaños</label>
          <input
            type="date"
            id="birthday"
            name="birthday"
            value={formData.birthday || ''}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Guardando...' : client?.id ? 'Actualizar' : 'Crear'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} disabled={loading}>
              Cancelar
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ClientForm;

